import React from "react";
import { useNavigate } from "react-router-dom";
import { useCompanyItems } from "../../items/hooks/useCompanyItems.js";
import { useCustomers } from "../../customers/hooks/useCustomers.js";
import { useInvoices } from "../../invoices/hooks/useInvoices.js";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { Card } from "../../../shared/components/Card.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";

export function DashboardPage({ user, onNavigate }) {
  const { items, loading: itemsLoading } = useCompanyItems();
  const { customers, loading: customersLoading } = useCustomers();
  const { invoices, loading: invoicesLoading } = useInvoices();
  const { companies, defaultCompany, loading: companiesLoading } = useCompanies();
  const navigate = useNavigate();


  const loading = itemsLoading || customersLoading || invoicesLoading || companiesLoading;

  const totalItems = items.length;
  const goodsItems = items.filter((i) => i.itemType !== "service");
  const totalStockUnits = goodsItems.reduce((sum, i) => sum + Number(i.stock || 0), 0);
  const lowStockItems = goodsItems.filter((i) => Number(i.stock) <= Number(i.reorderLevel || 10));
  const totalInventoryValue = goodsItems.reduce(
    (sum, i) => sum + (Number(i.basicPrice || i.sellingPrice || 0) * Number(i.stock || 0)),
    0
  );

  const totalBilled = invoices.reduce((sum, inv) => sum + Number(inv.grandTotal || 0), 0);
  const totalOutstanding = invoices.reduce((sum, inv) => sum + Number(inv.balance || 0), 0);

  return (
    <div className="dashboard-page">
      <div style={{ marginBottom: "1.25rem" }}>
        <p style={{ color: "var(--text-dim)", fontSize: "0.85rem", fontWeight: "600", textTransform: "uppercase" }}>
          Books Invoicing & Accounting
        </p>
        <h2 style={{ fontSize: "1.4rem", fontWeight: "700" }}>
          {user?.displayName || (user?.email ? user.email.split("@")[0] : "Admin")} 👋
        </h2>
      </div>

      {/* KPI tiles: stacked rows on phones, one row of five on desktop */}
      <div className="dashboard-kpis">
        {/* Financial Overview Metrics */}
        <div className="metrics-row">
          <div className="metric-card">
            <div className="metric-label">Total Revenue</div>
            <div className="metric-value" style={{ color: "#38bdf8" }}>
              ₹{loading ? "..." : totalBilled.toLocaleString("en-IN")}
            </div>
          </div>
          <div className="metric-card" style={{ borderLeft: totalOutstanding > 0 ? "3px solid var(--warning)" : "1px solid var(--border-subtle)" }}>
            <div className="metric-label">Outstanding Receivables</div>
            <div className="metric-value" style={{ color: totalOutstanding > 0 ? "var(--warning)" : "var(--success)" }}>
              ₹{loading ? "..." : totalOutstanding.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* Inventory & Asset Summary */}
        <div className="metrics-row">
          <div className="metric-card">
            <div className="metric-label">Stock Units</div>
            <div className="metric-value">
              {loading ? "..." : totalStockUnits.toLocaleString("en-IN")}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Parties / Customers</div>
            <div className="metric-value" style={{ color: "var(--primary)" }}>
              {loading ? "..." : customers.length}
            </div>
          </div>
        </div>

        <div className="metric-card" style={{ marginBottom: "1.25rem" }}>
          <div className="metric-label">Inventory Asset Valuation</div>
          <div className="metric-value" style={{ color: "var(--success)" }}>
            ₹{loading ? "..." : totalInventoryValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Needs Attention Alert */}
      {lowStockItems.length > 0 && (
        <Card style={{
          background: "rgba(245, 158, 11, 0.08)",
          borderColor: "rgba(245, 158, 11, 0.3)",
          marginBottom: "1.25rem"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: "700", color: "var(--warning)", display: "flex", alignItems: "center", gap: "6px" }}>
                <span>⚠️</span> {lowStockItems.length} {lowStockItems.length === 1 ? "Item is" : "Items are"} Low in Stock
              </div>
              <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "2px" }}>
                Reorder soon to maintain sales fulfillment.
              </div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onNavigate("inventory")}
            >
              Adjust Stock
            </Button>
          </div>
        </Card>
      )}

      {/* Quick Launchpad Grid */}
      <div style={{ marginBottom: "1.25rem" }}>
        <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: "0.5rem" }}>
          Quick Launchpad
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <Button variant="primary" block onClick={() => onNavigate("invoices")}>
            🧾 Sales Invoices
          </Button>
          <Button variant="secondary" block onClick={() => onNavigate("purchases")}>
            🛒 Vendor Purchases
          </Button>
          <Button variant="secondary" block onClick={() => onNavigate("inventory")}>
            📊 Stock & Movement
          </Button>
          <Button variant="secondary" block onClick={() => onNavigate("items")}>
            📦 Items Catalog
          </Button>
          <Button variant="secondary" block onClick={() => onNavigate("customers")}>
            👥 Party Directory
          </Button>
          <Button variant="secondary" block onClick={() => navigate("/settings#companies")}>
            🏢 Companies ({companies.length})
          </Button>
        </div>
      </div>

      {/* Recent Invoices Preview */}
      {invoices.length > 0 && (
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
            <span style={{ fontSize: "0.85rem", fontWeight: "700", color: "var(--text-dim)", textTransform: "uppercase" }}>
              Latest Sales Invoices
            </span>
            <button
              type="button"
              className="btn-icon"
              style={{ fontSize: "0.8rem", color: "var(--primary)" }}
              onClick={() => onNavigate("invoices")}
            >
              View all →
            </button>
          </div>

          {invoices.slice(0, 3).map((inv) => (
            <Card key={inv.id} style={{ padding: "0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <div>
                <div style={{ fontWeight: "600", fontSize: "0.92rem" }}>
                  {inv.invoiceNo} • {inv.customerName}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
                  {formatAppDate(inv.invoiceDate, inv.companyDetails?.dateFormat || defaultCompany?.dateFormat)} • {(inv.lineItems || []).length} items
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: "700", color: "#38bdf8" }}>
                  ₹{Number(inv.grandTotal || 0).toLocaleString("en-IN")}
                </div>
                <Badge variant={inv.paymentStatus === "Paid" ? "success" : "warning"}>
                  {inv.paymentStatus || "Unpaid"}
                </Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

    </div>
  );
}

export default DashboardPage;
